/**
 * Settings Panel Component
 * Main settings panel with collapsible sections
 */

import MathSettings from './MathSettings'
import WordSettings from './WordSettings'
import LettersSettings from './LettersSettings'
import FontSettings from './FontSettings'
import OpenAiAccessSettings from './OpenAiAccessSettings'
import { useEffect, useRef, useState } from 'react'

interface SettingsPanelProps {
  isOpen: boolean
  onSpeechConfigChange?: () => void
  onClose?: () => void
}

type SectionKey = 'view' | 'math' | 'word' | 'letters' | 'openai'
const STORAGE_KEY = 'practiceSettingsSections'

function loadSectionState(): Record<SectionKey, boolean> {
  if (typeof window === 'undefined') {
    return { view: true, math: false, word: true, openai: false, letters: true }
  }
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return { view: true, math: false, word: true, openai: false, letters: true }
  try {
    const parsed = JSON.parse(stored) as Partial<Record<SectionKey, boolean>>
    return {
      view: parsed.view ?? true,
      math: parsed.math ?? false,
      word: parsed.word ?? true,
      letters: parsed.letters ?? true,
      openai: parsed.openai ?? false,
    }
  } catch {
    return { view: true, math: false, word: true, openai: false, letters: true }
  }
}

export default function SettingsPanel({ isOpen, onSpeechConfigChange, onClose }: SettingsPanelProps) {
  const [sectionOpen, setSectionOpen] = useState<Record<SectionKey, boolean>>(loadSectionState)
  const panelRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sectionOpen))
  }, [sectionOpen])

  useEffect(() => {
    if (!isOpen) return

    const focusableSelectors = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')

    const focusPanel = () => {
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(focusableSelectors)
      const target = closeButtonRef.current ?? firstFocusable ?? panelRef.current
      target?.focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.()
        return
      }

      if (event.key !== 'Tab') return
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(focusableSelectors)
      const focusableElements = focusable
        ? Array.from(focusable).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1)
        : []

      if (focusableElements.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }

      const first = focusableElements[0]
      const last = focusableElements[focusableElements.length - 1]
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    focusPanel()
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const handleToggle = (section: SectionKey, open: boolean) => {
    setSectionOpen(prev => ({ ...prev, [section]: open }))
  }

  if (!isOpen) return null

  return (
    <div
      className="config-overlay"
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose?.()
        }
      }}
      role="presentation"
    >
      <section
        className="config"
        id="settingsPanel"
        aria-labelledby="settingsHeading"
        role="dialog"
        aria-modal="true"
        ref={panelRef}
        tabIndex={-1}
      >
        <h2 id="settingsHeading">Practice Settings</h2>
        {onClose && (
          <button
            type="button"
            className="config__close"
            onClick={onClose}
            aria-label="Close settings"
            ref={closeButtonRef}
          >
            ×
          </button>
        )}
        <div className="config__grid">
          <FontSettings isOpen={sectionOpen.view} onToggle={open => handleToggle('view', open)} />
          <MathSettings isOpen={sectionOpen.math} onToggle={open => handleToggle('math', open)} />
          <WordSettings isOpen={sectionOpen.word} onToggle={open => handleToggle('word', open)} />
          <LettersSettings isOpen={sectionOpen.letters} onToggle={open => handleToggle('letters', open)} />
          <OpenAiAccessSettings
            isOpen={sectionOpen.openai}
            onToggle={open => handleToggle('openai', open)}
            onClearVoiceCache={onSpeechConfigChange}
          />
        </div>
      </section>
    </div>
  )
}
